import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "@/lib/cn";
import { PreviewCheckMark } from "@/components/shapes/shared/card-preview-parts";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { todoFrameMessages } from "@/i18n/messages/frames-todo";
import type { TodoItem } from "./todo-items";
import type { TodoEdits } from "./use-todo-edits";

/**
 * One row of a checklist, opened (`card`) or on the desktop (`widget`): its
 * mark ticks it, its text is typed in place. Enter adds a row below; Backspace in an empty row removes it; a row
 * left empty is removed. Typed text saves when the row is left, not per
 * keystroke; an untouched row saves nothing, so an agent's change made while
 * it was focused stands.
 */
export function TodoEditRow({
  item,
  edits,
  focused,
  onFocusRow,
  previousId,
  appearance = "card",
  onEditingChange,
  testIdPrefix = "todo-frame",
}: {
  item: TodoItem;
  edits: TodoEdits;
  /** Focus this row's text now (it was just added, or the row below left). */
  focused: boolean;
  onFocusRow: (itemId: string | null) => void;
  previousId: string | null;
  appearance?: "card" | "widget";
  /** The row's text took or left focus. */
  onEditingChange?: (editing: boolean) => void;
  testIdPrefix?: string;
}) {
  const t = useT(todoFrameMessages);
  const [draft, setDraft] = useState(item.text);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Typed since focus or the last save: only then does leaving the row save.
  const dirtyRef = useRef(false);
  const done = item.done === true;

  useEffect(() => {
    if (!editing) setDraft(item.text);
  }, [item.text, editing]);

  function setEditingState(next: boolean) {
    setEditing(next);
    onEditingChange?.(next);
  }

  useEffect(() => {
    if (!focused) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
    onFocusRow(null);
  }, [focused, onFocusRow]);

  function commit() {
    const text = dirtyRef.current ? draft.trim() : item.text;
    dirtyRef.current = false;
    if (!text) edits.remove(item.id);
    else if (text !== item.text) edits.setText(item.id, text);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // An IME's own Enter (WebKit reports it as keyCode 229).
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === "Enter") {
      event.preventDefault();
      const text = dirtyRef.current ? draft.trim() : item.text;
      if (!text) return;
      dirtyRef.current = false;
      onFocusRow(
        edits.addAfter(item.id, text !== item.text ? text : undefined),
      );
    } else if (event.key === "Backspace" && draft === "") {
      event.preventDefault();
      dirtyRef.current = false;
      edits.remove(item.id);
      onFocusRow(previousId);
    } else if (event.key === "Escape") {
      event.preventDefault();
      dirtyRef.current = false;
      setDraft(item.text);
      inputRef.current?.blur();
    }
  }

  const widget = appearance === "widget";
  return (
    <div
      className={cn(
        "flex min-w-0 items-start",
        widget ? "gap-2" : "gap-[0.7em] text-card-ink",
      )}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={draft || t("task")}
        className="flex shrink-0 cursor-pointer"
        onClick={(event) => {
          // A tick acts in place and opens nothing.
          event.stopPropagation();
          edits.toggle(item.id);
        }}
        data-testid={`${testIdPrefix}-row-${item.id}`}
      >
        {widget ? (
          <WidgetCheckMark done={done} />
        ) : (
          <PreviewCheckMark done={done} />
        )}
      </button>
      <span className="min-w-0 flex-1">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          placeholder={t("task")}
          aria-label={t("task")}
          onChange={(event) => {
            dirtyRef.current = true;
            setDraft(event.target.value);
          }}
          onFocus={() => {
            dirtyRef.current = false;
            setEditingState(true);
          }}
          onBlur={() => {
            setEditingState(false);
            commit();
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            "block w-full bg-transparent leading-[inherit] outline-none",
            widget
              ? "placeholder:text-[color:var(--widget-muted,var(--card-muted))]"
              : "placeholder:text-card-muted",
            done &&
              (widget
                ? "line-through opacity-[.55]"
                : "text-card-muted line-through"),
          )}
          data-testid={`${testIdPrefix}-text-${item.id}`}
        />
        {!widget && item.detail && (
          <span className="block truncate text-[0.84em] text-card-muted">
            {item.detail}
          </span>
        )}
      </span>
    </div>
  );
}

/** A widget row's mark: ○ open, ● with a check when done, in the widget's ink. */
export function WidgetCheckMark({ done }: { done: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "mt-px grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border-[1.5px]",
        done
          ? "border-transparent bg-[var(--widget-ink,var(--card-ink))]"
          : "border-[color:var(--widget-muted,var(--card-muted))]",
      )}
    >
      {done && (
        <Icons.Check
          className="h-2.5 w-2.5 text-[color:var(--card-surface)]"
          strokeWidth={3.2}
        />
      )}
    </span>
  );
}
